/**
 * The L0 KPI strip at the top of the Cloud board (dashboard design rules §1):
 * four numbers that answer "is anything wrong right now?", each tile a door —
 * clicking it scrolls to the section that explains the number.
 *
 * The first tile is the one that matters most: subscriptions *reachable* out
 * of subscriptions *expected*. Azure Resource Graph only ever returns what the
 * caller can read, so without that ratio a permissions regression would render
 * as a smaller, healthier-looking estate. When it is short, the tile goes
 * danger and says so.
 */

import { Card, Statistic } from "@norican/react";
import type { ReactNode } from "react";

import type { CloudEstateSummary } from "@/lib/api";
import type { StatusTone } from "@/lib/charts/config";

import { formatCount, formatMoney } from "./status";

interface KpiTileProps {
  /** In-page anchor of the section that explains this number. */
  href: string;
  tone: StatusTone | "accent";
  children: ReactNode;
}

/** One KPI tile: a card that is also a link to its section (§4.1 — no dead aggregates). */
function KpiTile({ href, tone, children }: KpiTileProps) {
  return (
    <a href={href} className="block rounded focus-visible:outline-2">
      <Card variant="outline" accent="left" accentTone={tone} interactive>
        <Card.Body>{children}</Card.Body>
      </Card>
    </a>
  );
}

interface CloudKpiStripProps {
  summary: CloudEstateSummary;
}

export function CloudKpiStrip({ summary }: CloudKpiStripProps) {
  const allSubscriptionsVisible =
    summary.subscriptions_reachable === summary.subscriptions_expected;
  const missing = summary.subscriptions_expected - summary.subscriptions_reachable;
  const firingTone: StatusTone =
    summary.firing_alert_count == null
      ? "neutral"
      : summary.firing_alert_count > 0
        ? "danger"
        : "success";

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiTile href="#cloud-estate" tone={allSubscriptionsVisible ? "success" : "danger"}>
        <Statistic
          label="Subscriptions visible"
          value={`${summary.subscriptions_reachable} / ${summary.subscriptions_expected}`}
          tone={allSubscriptionsVisible ? "success" : "danger"}
          size="lg"
          caption={
            allSubscriptionsVisible
              ? "Every expected subscription answered"
              : `${missing} not readable — totals below are incomplete`
          }
        />
      </KpiTile>

      <KpiTile href="#cloud-estate" tone="accent">
        <Statistic
          label="Resources"
          value={formatCount(summary.resources)}
          size="lg"
          caption={`${formatCount(summary.resource_groups)} resource groups · ${formatCount(
            summary.resource_types,
          )} types`}
        />
      </KpiTile>

      <KpiTile href="#cloud-alerts" tone={firingTone}>
        <Statistic
          label="Alerts firing"
          value={formatCount(summary.firing_alert_count)}
          tone={firingTone}
          size="lg"
          caption={
            summary.firing_alert_count == null
              ? "Not collected yet — unknown is not zero"
              : summary.firing_alert_count > 0
                ? "Azure Monitor alerts firing right now"
                : "No Azure Monitor alerts firing"
          }
        />
      </KpiTile>

      <KpiTile href="#cloud-cost" tone="accent">
        <Statistic
          label="Cost month to date"
          value={formatMoney(summary.month_to_date_cost, summary.cost_currency)}
          size="lg"
          caption={
            summary.month_to_date_cost == null
              ? "Not collected yet"
              : "Actual Azure cost across the estate"
          }
        />
      </KpiTile>
    </div>
  );
}
